import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiDownload, FiChevronDown, FiEye, FiTrash2 } from 'react-icons/fi'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import MetricCard from '@/components/ui/MetricCard'
import StatusBadge from '@/components/ui/StatusBadge'
import {
  ChartSkeleton,
  ListCardSkeleton,
  NotificationSkeleton,
  Skeleton,
  TableSkeleton,
} from '@/components/ui/Skeleton'
import NewShipmentModal from '../shipments/NewShipmentModal'
import EditShipmentModal from '../shipments/EditShipmentModal'
import ShipmentDetailsModal from '@/components/ui/ShipmentDetailsModal'
import {
  useDashboardStats,
  useRecentShipments,
  useRecentDocuments,
  useInventoryStatus,
  useNotifications,
  useContractCompletion,
} from '@/hooks/useDashboard'
import { formatDateToDDMMMYYYY } from '@/utils/dateFormat'
import { SHIPMENT_STATUS_LABELS, shipmentToDisplay, type ShipmentDisplay } from '@/services/api/shipments'
import { getUser } from '@/utils/auth'
import apiService from '@/services/api'
import { openDocumentById, downloadDocumentById } from '@/services/api/documents'

const Dashboard = () => {
  const navigate = useNavigate()
  const [isNewShipmentModalOpen, setIsNewShipmentModalOpen] = useState(false)
  const [isEditShipmentModalOpen, setIsEditShipmentModalOpen] = useState(false)
  const [isShipmentDetailsModalOpen, setIsShipmentDetailsModalOpen] = useState(false)
  const [selectedShipment, setSelectedShipment] = useState<ShipmentDisplay | null>(null)
  const [timeframe, setTimeframe] = useState<'week' | 'month' | 'year'>('month')
  const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(null)

  // Fetch data from API
  const { stats, loading: statsLoading } = useDashboardStats()
  const { shipments: recentShipments, loading: shipmentsLoading } = useRecentShipments(5, 'active')
  const { documents: recentDocuments, loading: documentsLoading, refetch: refetchRecentDocuments } = useRecentDocuments(5)
  const { lots: inventoryLots, loading: inventoryLoading } = useInventoryStatus(5)
  const { notifications, loading: notificationsLoading } = useNotifications(false, 1, 4)
  const { data: contractData, loading: contractLoading } = useContractCompletion(timeframe)
  const currentUser = getUser()
  const canDeleteDocuments = currentUser?.role === 'admin'

  // Format trend text
  const formatTrend = (value: number, label: string) => {
    const sign = value >= 0 ? '↑' : '↓'
    const absValue = Math.abs(value).toFixed(2)
    return `${sign}${absValue}% ${label}`
  }

  // Format currency (South African Rand — matches Finance / Xero)
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatCount = (value: number) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)

  const formatWeightTon = (value: number) => {
    const safeKg = Number.isFinite(value) ? value : 0
    const tons = safeKg / 1000
    return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(tons)}\u00A0tons`
  }

  const handleViewDocument = async (documentId: string) => {
    try {
      await openDocumentById(documentId)
    } catch {
      window.alert('Could not open document. Please try again or download it instead.')
    }
  }

  const handleDownloadDocument = async (documentId: string, name: string) => {
    try {
      await downloadDocumentById(documentId, name)
    } catch {
      window.alert('Could not download document. Please try again.')
    }
  }

  // Format time ago
  const formatTimeAgo = (date: Date | string | undefined) => {
    if (!date) return 'recently'
    const now = new Date()
    const past = new Date(date)
    if (Number.isNaN(past.getTime())) return 'recently'
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000)
    if (diffInSeconds < 0) return 'Just now'

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`
    return `${Math.floor(diffInSeconds / 31536000)} years ago`
  }

  // Get notification color based on type
  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-500'
      case 'warning':
        return 'bg-yellow-500'
      case 'error':
        return 'bg-red-500'
      default:
        return 'bg-blue-500'
    }
  }

  // Get stock level color and label
  const getStockLevelInfo = (stockLevel: string) => {
    switch (stockLevel) {
      case 'critical':
        return { color: '#E93323', bgColor: '#FDEBE9', label: 'Critical', width: 20 }
      case 'low':
        return { color: '#E93323', bgColor: '#FDEBE9', label: 'Low Stock', width: 40 }
      case 'medium':
        return { color: '#EEC233', bgColor: '#FEF9EB', label: 'Moderate', width: 60 }
      case 'high':
        return { color: '#13AB5B', bgColor: '#E8F7EF', label: 'Sufficient', width: 85 }
      default:
        return { color: '#69697A', bgColor: '#F2F4F2', label: 'Unknown', width: 50 }
    }
  }

  // Map raw shipment status to a human-readable label (shared with shipments)
  const getStatusLabel = (status: string): string => {
    const normalized = status?.toLowerCase().trim() || ''
    return SHIPMENT_STATUS_LABELS[normalized] ?? status
  }

  // Map database shipment status to StatusBadge-compatible props
  const getStatusBadgeProps = (
    status: string,
  ): { status: 'in_transit' | 'delayed' | 'arrived' | 'pending' | 'completed'; label: string } => {
    const normalized = status?.toLowerCase() || ''

    switch (normalized) {
      case 'in_transit':
        return { status: 'in_transit', label: 'In Transit' }
      case 'delayed':
        return { status: 'delayed', label: 'Delayed' }
      case 'arrived':
        return { status: 'arrived', label: 'Arrived' }
      case 'cleared':
      case 'unpacked':
      case 'in_storage':
      case 'fully_delivered':
        return { status: 'completed', label: getStatusLabel(normalized) }
      case 'pending':
      case 'in_coordination':
      default:
        return { status: 'pending', label: getStatusLabel(normalized) }
    }
  }

  const handleDeleteRecentDocument = async (documentId: string, documentName: string) => {
    if (!canDeleteDocuments) return
    if (!window.confirm(`Delete document "${documentName}"?`)) return
    try {
      setDeletingDocumentId(documentId)
      await apiService.delete(`/documents/${documentId}`)
      await refetchRecentDocuments()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete document'
      window.alert(message)
    } finally {
      setDeletingDocumentId(null)
    }
  }

  const escapeCsv = (value: unknown): string => {
    const text = value == null ? '' : String(value)
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`
    return text
  }

  const downloadCsv = (filename: string, headers: string[], rows: Array<Array<unknown>>) => {
    const csv = [headers.join(','), ...rows.map((row) => row.map(escapeCsv).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const exportActiveShipments = () => {
    downloadCsv(
      `dashboard-active-shipments-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Container Number', 'Shipping Line', 'Origin', 'Destination', 'ETD', 'ETA', 'Status'],
      recentShipments.map((shipment) => [
        shipment.containerNumber || 'N/A',
        shipment.carrier || 'N/A',
        shipment.pol || 'N/A',
        shipment.pod || 'N/A',
        shipment.etd ? formatDateToDDMMMYYYY(shipment.etd.toString()) : 'N/A',
        shipment.eta ? formatDateToDDMMMYYYY(shipment.eta.toString()) : 'N/A',
        getStatusLabel(shipment.status),
      ])
    )
  }

  const exportContractCompletion = () => {
    downloadCsv(
      `dashboard-contract-completion-${timeframe}-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Period', 'Current', 'Prior'],
      (contractData?.chartData || []).map((point) => [point.month, point.current, point.prior])
    )
  }

  const exportRecentDocuments = () => {
    downloadCsv(
      `dashboard-recent-documents-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Name', 'Uploaded By', 'Uploaded At', 'URL'],
      recentDocuments.map((doc) => [doc.name, doc.user?.name || doc.user?.email || 'N/A', doc.uploadedAt, doc.url])
    )
  }

  const exportInventoryStatus = () => {
    downloadCsv(
      `dashboard-inventory-status-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Lot Number', 'SKU', 'Location', 'Units', 'Weight (kg)', 'Stock Level', 'Updated At'],
      inventoryLots.map((lot) => [
        lot.lotNumber,
        lot.sku || 'N/A',
        lot.location || 'N/A',
        lot.bags ?? lot.pieces ?? lot.balance ?? 0,
        lot.weight ?? '',
        lot.stockLevel,
        lot.updatedAt,
      ])
    )
  }

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-lg sm:text-xl"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              lineHeight: '32px',
              letterSpacing: '0%',
              color: '#212126'
            }}
          >
            Dashboard
          </h1>
          <p
            className="mt-1 text-xs sm:text-sm"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              lineHeight: '20px',
              letterSpacing: '0%',
              color: '#69697A'
            }}
          >
            Overview of your logistics operations
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-3">
          {/* Export Button */}
          <button
            onClick={exportActiveShipments}
            className="flex items-center justify-center gap-2 sm:gap-3 font-medium rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap w-full sm:w-auto"
            style={{
              height: '38px',
              color: '#212126',
              border: '1px solid #212126',
              padding: '8px 20px',
              borderRadius: '6px',
              minWidth: '100px'
            }}
          >
            <img
              src="/images/download.svg"
              alt="Download"
              className="w-4 h-4"
              style={{
                filter: 'brightness(0) saturate(100%) invert(8%) sepia(4%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)'
              }}
            />
            <span className="text-sm sm:text-base">Export</span>
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-4"
        style={{
          width: '100%'
        }}
      >
        <MetricCard
          title="Active Shipments"
          value={stats ? stats.activeShipments.toString() : '0'}
          trend={stats ? formatTrend(stats.activeShipmentsTrend, 'from last month') : ''}
          loading={statsLoading && !stats}
          icon={<img src="/images/ship.svg" alt="Ship" className="w-6 h-6" />}
          iconBgColor="bg-primary-100"
          iconBgColorCustom="#F2F4F2"
          style={{ width: '100%', minHeight: '120px', height: 'auto' }}
        />
        <MetricCard
          title="Pending Documents"
          value={stats ? stats.pendingDocuments.toString() : '0'}
          trend={stats ? formatTrend(stats.pendingDocumentsTrend, 'from last week') : ''}
          loading={statsLoading && !stats}
          icon={<img src="/images/pending-document.svg" alt="Pending Documents" className="w-6 h-6" />}
          iconBgColor="bg-yellow-50"
          style={{ width: '100%', minHeight: '120px', height: 'auto' }}
        />
        <MetricCard
          title="Stock Levels"
          value={stats ? formatWeightTon(stats.stockTotalWeight ?? 0) : formatWeightTon(0)}
          trend={stats ? formatTrend(stats?.stockTotalWeightTrend ?? stats.lowStockTrend, 'from last month') : ''}
          loading={statsLoading && !stats}
          icon={<img src="/images/stockLevel.svg" alt="Stock Levels" className="w-5 h-5" />}
          iconBgColor="bg-red-50"
          style={{ width: '100%', minHeight: '120px', height: 'auto' }}
        />
        <MetricCard
          title="Pending Payments"
          value={stats ? formatCurrency(stats.pendingPayments) : formatCurrency(0)}
          trend={stats ? formatTrend(stats.pendingPaymentsTrend, 'from last month') : ''}
          loading={statsLoading && !stats}
          icon={<img src="/images/dollar-circle.svg" alt="Dollar" style={{ width: '20px', height: '20px' }} />}
          iconBgColor="bg-amber-50"
          iconBgColorCustom="#EAF1FF"
          style={{ width: '100%', minHeight: '120px', height: 'auto' }}
        />
      </div>

      {/* Contract Completion Overview and Recent Notifications Row */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Contract Completion Overview Card - 75% width (3 metric cards) */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 w-full lg:w-[calc(75%-12px)]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h3
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: 'clamp(13px, 3vw, 14px)',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126',
              }}
            >
              Contract Completion Overview
            </h3>
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              <div className="relative flex items-center">
                <img
                  src="/images/calendar.svg"
                  alt="Calendar"
                  className="absolute left-3 pointer-events-none"
                  style={{ width: '18px', height: '18px', opacity: 1 }}
                />
                <select
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value as 'week' | 'month' | 'year')}
                  className="pl-10 pr-8 py-1 border border-gray-300 rounded text-sm appearance-none"
                >
                  <option value="month">Month</option>
                  <option value="week">Week</option>
                  <option value="year">Year</option>
                </select>
                <FiChevronDown
                  className="absolute right-3 pointer-events-none"
                  style={{ width: '16px', height: '16px', opacity: 1, color: '#212126' }}
                />
              </div>
              <button
                onClick={exportContractCompletion}
                className="flex items-center justify-center gap-1 hover:opacity-90 transition-opacity whitespace-nowrap"
                style={{
                  width: '65px',
                  height: '34px',
                  borderRadius: '6px',
                  paddingTop: '8px',
                  paddingRight: '12px',
                  paddingBottom: '8px',
                  paddingLeft: '12px',
                  backgroundColor: '#212126',
                  opacity: 1,
                }}
              >
                <FiDownload
                  className="w-4 h-4"
                  style={{ color: '#FFFFFF' }}
                />
                <span
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: '#FFFFFF',
                  }}
                >
                  Export
                </span>
              </button>
            </div>
          </div>
          <div className="mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
              <div className="flex flex-col">
                <p
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: 'clamp(18px, 4vw, 20px)',
                    lineHeight: '32px',
                    letterSpacing: '0%',
                    color: '#69697A',
                  }}
                >
                  {contractLoading && !contractData ? (
                    <Skeleton style={{ width: 72, height: 24 }} />
                  ) : (
                    formatCount(contractData?.currentTotal ?? 0)
                  )}
                </p>
                {contractData != null && (contractData.currentTonnage ?? 0) > 0 && (
                  <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '18px', color: '#69697A' }}>
                    {(contractData.currentTonnage ?? 0).toLocaleString()} t
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-1">
                  <span
                    style={{
                      fontFamily: 'Lexend Deca',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: 'clamp(11px, 2.5vw, 12px)',
                      lineHeight: '16px',
                      letterSpacing: '0%',
                      color: '#13AB5B',
                    }}
                  >
                    Current Period Total
                  </span>
                  {contractData && contractData.percentageChange > 0 && (
                    <div
                      className="flex items-center"
                      style={{
                        width: 'auto',
                        height: '20px',
                        borderRadius: '20px',
                        paddingTop: '2px',
                        paddingRight: '4px',
                        paddingBottom: '2px',
                        paddingLeft: '4px',
                        gap: '2px',
                        backgroundColor: '#E8F7EF',
                        opacity: 1,
                        flexShrink: 0,
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ opacity: 1 }}
                      >
                        <path
                          d="M7 11.6667L2.33333 7L3.5 5.83333L7 9.33333L10.5 5.83333L11.6667 7L7 11.6667Z"
                          fill="none"
                          stroke="#13AB5B"
                          strokeWidth="0.88"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontStyle: 'normal',
                          fontSize: '12px',
                          lineHeight: '16px',
                          letterSpacing: '0%',
                          color: '#13AB5B',
                        }}
                      >
                        Up {Math.abs(contractData.percentageChange).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col">
                <p
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: 'clamp(18px, 4vw, 20px)',
                    lineHeight: '32px',
                    letterSpacing: '0%',
                    color: '#69697A',
                  }}
                >
                  {contractLoading && !contractData ? (
                    <Skeleton style={{ width: 72, height: 24 }} />
                  ) : (
                    formatCount(contractData?.priorTotal ?? 0)
                  )}
                </p>
                {contractData != null && (contractData.priorTonnage ?? 0) > 0 && (
                  <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '18px', color: '#69697A' }}>
                    {(contractData.priorTonnage ?? 0).toLocaleString()} t
                  </p>
                )}
                <p
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontStyle: 'normal',
                    fontSize: 'clamp(11px, 2.5vw, 12px)',
                    lineHeight: '18px',
                    letterSpacing: '0%',
                    color: '#69697A',
                  }}
                >
                  Prior Period Total
                </p>
              </div>
            </div>
            {/* Current and Prior Legend - aligned with Total */}
            <div className="flex items-center gap-4 sm:gap-4" style={{ alignSelf: 'flex-start sm:flex-end', paddingTop: '0 sm:14px' }}>
              <div className="flex items-center gap-2">
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#2C71F6',
                    display: 'inline-block',
                  }}
                />
                <span
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: 'clamp(11px, 2.5vw, 12px)',
                    lineHeight: '16px',
                    color: '#69697A',
                  }}
                >
                  Current
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#F3897F',
                    display: 'inline-block',
                  }}
                />
                <span
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: 'clamp(11px, 2.5vw, 12px)',
                    lineHeight: '16px',
                    color: '#69697A',
                  }}
                >
                  Prior
                </span>
              </div>
            </div>
          </div>
          <div className="w-full" style={{ height: 'clamp(250px, 40vw, 300px)' }}>
            {contractLoading && !contractData ? (
              <ChartSkeleton />
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={contractData?.chartData || []}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#E5E7EB"
                  horizontal={true}
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  stroke="#6B7280"
                  axisLine={{
                    stroke: '#E9E9EA',
                    strokeWidth: 1,
                  }}
                  tickLine={false}
                  tick={{
                    fill: '#050521',
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '14px',
                  }}
                />
                <YAxis
                  stroke="#6B7280"
                  tick={{
                    fill: '#69697A',
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '12px',
                  }}
                  tickFormatter={(value) => formatCount(Number(value))}
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '16px',
                  }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip formatter={(value: number | string) => [`${formatCount(Number(value))} fulfillments`, '']} />
                <Bar
                  dataKey="current"
                  fill="#2C71F6"
                  name="Current"
                  radius={[8, 8, 0, 0]}
                  barSize={10}
                />
                <Bar
                  dataKey="prior"
                  fill="#F3897F"
                  name="Prior"
                  radius={[8, 8, 0, 0]}
                  barSize={10}
                />
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Recent Notifications - 25% width (1 metric card), aligned below Pending Payments */}
        <div
          className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 w-full lg:w-[calc(25%-12px)]"
        >
          <h2
            className="mb-4 text-sm sm:text-base"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              lineHeight: '28px',
              letterSpacing: '0%',
              color: '#212126'
            }}
          >
            Recent Notifications
          </h2>
          {/* Full-width separator line - covers entire card width */}
          <div
            style={{
              position: 'relative',
              width: 'calc(100% + 48px)',
              height: '0px',
              borderTop: '1px solid #E9E9EA',
              marginBottom: '16px',
              marginLeft: '-24px',
              marginRight: '-24px',
              left: '0',
              right: '0'
            }}
          ></div>
          <div>
            {notificationsLoading && notifications.length === 0 ? (
              <NotificationSkeleton rows={4} />
            ) : notifications.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No notifications</div>
            ) : (
              notifications.map((notification, index) => (
                <div key={notification.id}>
                  {index > 0 && (
                    <div
                      style={{
                        width: '100%',
                        height: '0px',
                        borderTop: '1px solid #E9E9EA',
                        marginBottom: '16px'
                      }}
                    ></div>
                  )}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (notification.link) navigate(notification.link)
                    }}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && notification.link) {
                        e.preventDefault()
                        navigate(notification.link!)
                      }
                    }}
                    className={`flex items-start gap-3 ${index < notifications.length - 1 ? 'pb-4' : ''} ${notification.link ? 'cursor-pointer hover:bg-gray-50 rounded-md -mx-1 px-1 py-1 transition-colors' : ''}`}
                  >
                    <div className={`w-2 h-2 rounded-full ${getNotificationColor(notification.type)} mt-2 flex-shrink-0`}></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{formatTimeAgo(notification.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Active Shipments Table - Full Width */}
      <div
        className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 w-full"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2
            className="text-sm sm:text-base"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              lineHeight: '28px',
              letterSpacing: '0%',
              color: '#212126'
            }}
          >
            Active Shipments
          </h2>
          <div className="relative w-full sm:w-auto">
            <img
              src="/images/calendar.svg"
              alt="Calendar"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none"
            />
            <select className="appearance-none bg-white border border-gray-300 rounded-lg pl-10 pr-8 py-2 text-sm text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-auto">
              <option>Month</option>
              <option>January</option>
              <option>February</option>
              <option>March</option>
              <option>April</option>
              <option>May</option>
              <option>June</option>
              <option>July</option>
              <option>August</option>
              <option>September</option>
              <option>October</option>
              <option>November</option>
              <option>December</option>
            </select>
            <FiChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
        </div>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle px-4 sm:px-0">
            <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto', width: '100%', minWidth: '800px' }}>
              <thead>
                <tr>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-l border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '120px', maxWidth: '150px', borderTopLeftRadius: '8px' }}>Container Number</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '110px', maxWidth: '140px' }}>Shipping Line</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '90px', maxWidth: '120px' }}>Origin</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '110px', maxWidth: '140px' }}>Destination</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 whitespace-nowrap border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '100px' }}>ETD</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 whitespace-nowrap border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '100px' }}>ETA</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '100px' }}>Status</th>
                  <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 border-r border-t border-b border-gray-200" style={{ width: 'auto', minWidth: '100px', paddingLeft: '24px', borderTopRightRadius: '8px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {shipmentsLoading && recentShipments.length === 0 ? (
                  <TableSkeleton rows={5} cols={8} />
                ) : recentShipments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-500 border-b border-gray-200">
                      No active shipments found
                    </td>
                  </tr>
                ) : (
                  recentShipments.map((shipment, index) => {
                    const shipmentData = shipmentToDisplay({
                      id: shipment.id,
                      booking: shipment.booking,
                      bl: shipment.bl,
                      billOfLading: shipment.bl,
                      containerNumber: shipment.containerNumber,
                      vessel: shipment.vessel,
                      carrier: shipment.carrier,
                      pol: shipment.pol,
                      pod: shipment.pod,
                      etd: shipment.etd?.toString(),
                      eta: shipment.eta?.toString(),
                      status: shipment.status,
                    })

                    const isLastRow = index === recentShipments.length - 1

                    return (
                      <tr key={shipment.id} className="hover:bg-gray-50">
                        <td
                          className="py-3 px-2 text-sm text-gray-900 border-l border-b border-gray-200"
                          style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '150px',
                            ...(isLastRow ? { borderBottomLeftRadius: '8px' } : {})
                          }}
                        >
                          {shipment.containerNumber || 'N/A'}
                        </td>
                        <td
                          className="py-3 px-2 text-sm text-gray-600 border-b border-gray-200"
                          style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '140px'
                          }}
                        >
                          {shipment.carrier || 'N/A'}
                        </td>
                        <td
                          className="py-3 px-2 text-sm text-gray-600 border-b border-gray-200"
                          style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '120px'
                          }}
                        >
                          {shipment.pol || 'N/A'}
                        </td>
                        <td
                          className="py-3 px-2 text-sm text-gray-600 border-b border-gray-200"
                          style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '140px'
                          }}
                        >
                          {shipment.pod || 'N/A'}
                        </td>
                        <td className="py-3 px-2 text-sm text-gray-600 whitespace-nowrap border-b border-gray-200">
                          {shipment.etd ? formatDateToDDMMMYYYY(shipment.etd.toString()) : 'N/A'}
                        </td>
                        <td className="py-3 px-2 text-sm text-gray-600 whitespace-nowrap border-b border-gray-200">
                          {shipment.eta ? formatDateToDDMMMYYYY(shipment.eta.toString()) : 'N/A'}
                        </td>
                        <td className="py-3 px-2 border-b border-gray-200">
                          {(() => {
                            const statusBadgeProps = getStatusBadgeProps(shipment.status)
                            return <StatusBadge status={statusBadgeProps.status} label={statusBadgeProps.label} />
                          })()}
                        </td>
                        <td className="py-3 px-2 border-r border-b border-gray-200" style={{ paddingLeft: '24px', ...(isLastRow ? { borderBottomRightRadius: '8px' } : {}) }}>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedShipment(shipmentData)
                                setIsEditShipmentModalOpen(true)
                              }}
                              className="p-1 transition-all flex items-center justify-center cursor-pointer hover:opacity-80 hover:shadow-sm"
                              style={{
                                backgroundColor: '#FEF9EB',
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#F5E6C8'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#FEF9EB'
                              }}
                            >
                              <img src="/images/linearDots.svg" alt="More options" className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedShipment(shipmentData)
                                setIsShipmentDetailsModalOpen(true)
                              }}
                              className="p-1 transition-all flex items-center justify-center cursor-pointer hover:opacity-80 hover:shadow-sm"
                              style={{
                                backgroundColor: '#EAF1FF',
                                width: '24px',
                                height: '24px',
                                borderRadius: '4px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#D4E3FF'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#EAF1FF'
                              }}
                            >
                              <img src="/images/eye.svg" alt="View" className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bottom Section - Recent Documents and Inventory Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Documents */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2
              className="text-sm sm:text-base"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126'
              }}
            >
              Recent Documents
            </h2>
            <button
              onClick={exportRecentDocuments}
              className="flex items-center justify-center gap-2 hover:opacity-90 transition-opacity w-full sm:w-auto"
              style={{
                height: '34px',
                borderRadius: '6px',
                paddingTop: '8px',
                paddingRight: '12px',
                paddingBottom: '8px',
                paddingLeft: '12px',
                backgroundColor: '#212126',
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: '20px',
                letterSpacing: '0%',
                color: '#FFFFFF',
                minWidth: '65px'
              }}
            >
              <FiDownload className="w-4 h-4" style={{ color: '#FFFFFF' }} />
              <span>Export</span>
            </button>
          </div>
          <div className="space-y-4">
            {documentsLoading && recentDocuments.length === 0 ? (
              <ListCardSkeleton rows={4} />
            ) : recentDocuments.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No recent documents</div>
            ) : (
              recentDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => void handleViewDocument(doc.id)}
                      className="p-0 border-0 bg-transparent cursor-pointer rounded flex items-center justify-center text-gray-600 hover:text-gray-900"
                      title="View document"
                    >
                      <FiEye className="w-5 h-5" style={{ color: '#2C71F6' }} />
                    </button>
                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                      <img src="/images/clearing-status.svg" alt="Document" className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {doc.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Added {formatTimeAgo(doc.uploadedAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void handleDownloadDocument(doc.id, doc.name)}
                      className="p-2 text-gray-600 hover:text-gray-900 rounded flex items-center justify-center"
                      style={{
                        backgroundColor: '#E8F7EF'
                      }}
                      title="Download document"
                    >
                      <img src="/images/download.svg" alt="Download" className="w-4 h-4" />
                    </button>
                    {canDeleteDocuments && (
                      <button
                        type="button"
                        onClick={() => handleDeleteRecentDocument(doc.id, doc.name)}
                        disabled={deletingDocumentId === doc.id}
                        className="p-2 rounded flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-50"
                        style={{
                          backgroundColor: '#FEE2E2',
                          color: '#EF4444'
                        }}
                        title="Delete document"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Inventory Status */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2
              className="text-sm sm:text-base"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126'
              }}
            >
              Inventory Status
            </h2>
            <button
              onClick={exportInventoryStatus}
              className="flex items-center justify-center gap-2 hover:opacity-90 transition-opacity w-full sm:w-auto"
              style={{
                height: '34px',
                borderRadius: '6px',
                paddingTop: '8px',
                paddingRight: '12px',
                paddingBottom: '8px',
                paddingLeft: '12px',
                backgroundColor: '#212126',
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: '20px',
                letterSpacing: '0%',
                color: '#FFFFFF',
                minWidth: '65px'
              }}
            >
              <FiDownload className="w-4 h-4" style={{ color: '#FFFFFF' }} />
              <span>Export</span>
            </button>
          </div>
          <div className="space-y-4">
            {inventoryLoading && inventoryLots.length === 0 ? (
              <ListCardSkeleton rows={4} />
            ) : inventoryLots.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No stock level alerts</div>
            ) : (
              inventoryLots.map((lot) => {
                const stockInfo = getStockLevelInfo(lot.stockLevel)
                const units = lot.bags ?? lot.pieces ?? lot.balance ?? 0

                return (
                  <div key={lot.id} className="p-3 border border-gray-100 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEF4F3' }}>
                          <img src="/images/box.svg" alt="Box" className="w-5 h-5" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">{lot.lotNumber}</span>
                          <span className="text-xs text-gray-500 mt-1">{lot.sku || 'N/A'}{lot.location ? ` · ${lot.location}` : ''}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end flex-1 ml-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-24 bg-gray-200 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full"
                              style={{
                                width: `${stockInfo.width}%`,
                                backgroundColor: stockInfo.color
                              }}
                            ></div>
                          </div>
                          <div className="flex flex-col items-start">
                            <span className="text-sm text-gray-600">{units} Units{lot.weight ? ` · ${lot.weight} kg` : ''}</span>
                            <span
                              className="px-2 py-1 rounded mt-1"
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 300,
                                fontStyle: 'normal',
                                fontSize: '12px',
                                lineHeight: '16px',
                                letterSpacing: '0%',
                                color: stockInfo.color,
                                backgroundColor: stockInfo.bgColor
                              }}
                            >
                              {stockInfo.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>

      {/* New Shipment Modal */}
      <NewShipmentModal
        isOpen={isNewShipmentModalOpen}
        onClose={() => setIsNewShipmentModalOpen(false)}
      />

      {/* Edit Shipment Modal */}
      <EditShipmentModal
        isOpen={isEditShipmentModalOpen}
        onClose={() => {
          setIsEditShipmentModalOpen(false)
          setSelectedShipment(null)
        }}
        shipmentData={selectedShipment || undefined}
      />

      {/* Shipment Details Modal */}
      <ShipmentDetailsModal
        isOpen={isShipmentDetailsModalOpen}
        onClose={() => {
          setIsShipmentDetailsModalOpen(false)
          setSelectedShipment(null)
        }}
        shipmentData={selectedShipment || undefined}
      />
    </div>
  )
}

export default Dashboard
